import React, { useEffect, useState } from "react";

import * as api from "../../api";
import { seatErrorMessage, seatStateChip } from "../../utils/table";
import TableSeatForm from "./TableSeatForm";
import { ReactComponent as CheckIcon } from "../../assets/images/checkmark.svg";
import { ReactComponent as AlertIcon } from "../../assets/images/input-error.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-addons.svg";
import { ReactComponent as TrashIcon } from "../../assets/images/trashcan.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/plus-black.svg";

/**
 * One seat of a table.
 *
 * `isEditable` comes from the server and already accounts for every reason a
 * seat is frozen — roster locked, guest arrived, table unpaid or cancelled — so
 * controls are disabled from it rather than from local guesswork. The 409 code
 * mapping is the safety net, not the primary mechanism.
 *
 * Still no optimistic updates: assigning a guest re-points a ticket and triggers
 * delivery, so this component never guesses the outcome. What it does pass up is
 * the seat the SERVER returned, which the parent applies before its own reload
 * lands — the reload is then only for the aggregates it alone can compute.
 *
 * NOTE `isHost` is false on EVERY seat of a web-bought table — the buyer is not
 * a seat holder there, because the checkout path gives every order item a dummy
 * customer. Only admin/VIP tables have a host seat, so nothing here may assume
 * one exists.
 */

/** Initials for the seat marker: a name if there is one, else the seat number. */
const initialsFor = (seat) => {
  const letters = [seat.firstName, seat.lastName]
    .filter(Boolean)
    .map((part) => part.trim()[0])
    .join("");
  if (letters) return letters.toUpperCase();
  const fromName = (seat.name || "").trim();
  if (fromName) return fromName[0].toUpperCase();
  return String(seat.position);
};

const TableSeatRow = ({ seat, tableCode, token, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);

  // A resend leaves nothing visible behind on the seat, so it gets its own
  // confirmation — dismissed on a timer rather than left on screen forever.
  useEffect(() => {
    if (!resent) return undefined;
    const timer = setTimeout(() => setResent(false), 5000);
    return () => clearTimeout(timer);
  }, [resent]);

  const busy = pending !== null;

  const run = (action, promise, onDone) => {
    setPending(action);
    setError("");
    setResent(false);
    return promise
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          setEditing(false);
          setConfirmingRemove(false);
          if (onDone) onDone();
          // Hand the parent the seat the SERVER just returned. Not a guess --
          // assign and clear both respond with TableSeatSerializer(seat) -- so
          // the row updates even before the table reload lands, and survives a
          // reload that fails. Depending on the reload alone meant a change the
          // server had already made did not appear until the host refreshed.
          onChanged(response.data);
        } else {
          setError(seatErrorMessage(null));
        }
      })
      .catch((err) => setError(seatErrorMessage(err)))
      .finally(() => setPending(null));
  };

  const handleSave = (guest) =>
    run("save", api.tables.assignSeat(tableCode, seat.position, guest, token));
  const handleRemove = () =>
    run("remove", api.tables.clearSeat(tableCode, seat.position, token));
  const handleResend = () =>
    run(
      "resend",
      api.tables.resendSeatPass(tableCode, seat.position, token),
      () => setResent(true),
    );

  const seatLabel = `seat ${seat.position}`;

  if (seat.isHost) {
    return (
      <li className="table-seat table-seat--host">
        <div className="table-seat__main">
          <span className="table-seat__avatar" aria-hidden="true">
            {initialsFor(seat)}
          </span>
          <span className="table-seat__identity">
            <span className="table-seat__name">{seat.name}</span>
            <span className="table-seat__contact">
              <span>Seat {seat.position}</span>
            </span>
          </span>
          <span className="table-seat__chip table-seat__chip--arrived">
            You
          </span>
        </div>
      </li>
    );
  }

  const isEmpty = !seat.name;
  const chip = seatStateChip(seat);
  const frozen = !seat.isEditable;

  return (
    <li className={`table-seat${isEmpty ? " table-seat--empty" : ""}`}>
      <div className="table-seat__main">
        <span
          className={`table-seat__avatar${
            isEmpty ? " table-seat__avatar--empty" : ""
          }`}
          aria-hidden="true"
        >
          {initialsFor(seat)}
        </span>

        <span className="table-seat__identity">
          {isEmpty ? (
            // The avatar already carries the number, so the row says the seat
            // once and spends the rest of the line on its state.
            <>
              <span className="table-seat__name table-seat__name--empty">
                Seat {seat.position}
              </span>
              <span className="table-seat__contact">
                <span>No guest yet</span>
              </span>
            </>
          ) : (
            <>
              <span className="table-seat__name">
                {seat.name}
                {/* The buyer's own seat. A marker beside the name, not a status
                    chip: it says whose seat it is, while the chip says what
                    happened to the pass. It also must not read as frozen -- this
                    seat is pre-filled but still theirs to hand over, which is
                    why is_host (the flag that locks a seat) stays false. */}
                {seat.isBuyer ? (
                  <span className="table-seat__you">You</span>
                ) : null}
              </span>
              <span className="table-seat__contact">
                <span>Seat {seat.position}</span>
                {seat.mobileNumber ? (
                  <span className="table-seat__phone">{seat.mobileNumber}</span>
                ) : null}
                {seat.email ? (
                  <span className="table-seat__email">{seat.email}</span>
                ) : null}
              </span>
            </>
          )}
        </span>

        {/* One badge, decided by seatStateChip. Every state says what it is in
            words; the colour only reinforces it. */}
        {/* An open seat whose controls are dead needs to say so, or the
            disabled button reads as a broken page. */}
        {isEmpty && frozen ? (
          <span className="table-seat__chip">Locked</span>
        ) : null}
        {chip ? (
          <span
            className={`table-seat__chip${
              chip.tone ? ` table-seat__chip--${chip.tone}` : ""
            }`}
          >
            {chip.icon === "check" ? <CheckIcon aria-hidden="true" /> : null}
            {chip.icon === "alert" ? <AlertIcon aria-hidden="true" /> : null}
            {chip.label}
          </span>
        ) : null}

        {/* An open seat has exactly one thing to do, so it sits on the row
            itself rather than in an action strip of its own -- ten empty seats
            would otherwise be ten full-width bars. */}
        {isEmpty && !editing ? (
          <button
            type="button"
            className="table-seat__action table-seat__action--add"
            onClick={() => setEditing(true)}
            disabled={frozen || busy}
            aria-label={`Add guest to ${seatLabel}`}
          >
            <PlusIcon aria-hidden="true" />
            Add guest
          </button>
        ) : null}
      </div>

      {editing ? (
        <TableSeatForm
          seat={seat}
          onSubmit={handleSave}
          onCancel={() => {
            setEditing(false);
            setError("");
          }}
          submitting={pending === "save"}
          errorMessage={error}
        />
      ) : (
        <>
          {error ? (
            <span className="table-seat__error" role="alert">
              <AlertIcon aria-hidden="true" />
              {error}
            </span>
          ) : null}

          {resent ? (
            <span className="table-seat__sent" role="status">
              <CheckIcon aria-hidden="true" />
              Pass sent again to {seat.name}.
            </span>
          ) : null}

          {isEmpty ? null : (
            <div className="table-seat__actions">
              {/* Removing a guest revokes a pass that is already in their hands,
                so it asks first rather than acting on a mis-tap. */}
              {confirmingRemove ? (
                <div className="table-seat__confirm">
                  <span className="table-seat__confirm-text">
                    Remove {seat.name}? Their pass stops working and the seat
                    opens up again.
                  </span>
                  <button
                    type="button"
                    className="table-seat__action table-seat__action--danger"
                    onClick={handleRemove}
                    disabled={busy}
                  >
                    {pending === "remove" ? "Removing…" : "Yes, remove"}
                  </button>
                  <button
                    type="button"
                    className="table-seat__action"
                    onClick={() => setConfirmingRemove(false)}
                    disabled={busy}
                  >
                    Keep guest
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="table-seat__action"
                    onClick={() => setEditing(true)}
                    disabled={frozen || busy}
                    aria-label={`Edit ${seat.name}`}
                  >
                    <EditIcon aria-hidden="true" />
                    Edit
                  </button>
                  {/* Deliberately NOT gated on isEditable: the backend allows a
                    resend on a frozen roster, which is exactly when a guest
                    reports a lost pass. */}
                  <button
                    type="button"
                    className="table-seat__action"
                    onClick={handleResend}
                    disabled={busy}
                    aria-label={`Resend pass to ${seat.name}`}
                  >
                    {pending === "resend" ? "Sending…" : "Resend"}
                  </button>
                  <button
                    type="button"
                    className="table-seat__action table-seat__action--danger"
                    onClick={() => setConfirmingRemove(true)}
                    disabled={frozen || busy}
                    aria-label={`Remove ${seat.name}`}
                  >
                    <TrashIcon aria-hidden="true" />
                    Remove
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </li>
  );
};

export default TableSeatRow;
